import { useEffect, useState } from "react";
import { Plus, X, Pencil, Trash2, Power, Route, Loader2, ArrowLeftRight, ArrowRight } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Eyebrow, PageTitle, PageSubtitle, Loader, EmptyState, Card, BackHeader } from "@/components/common";
import { cn } from "@/lib/utils";

const RADII = [10, 20, 30, 50, 100];
const DIRECTIONS = [
  ["andata", "Andata"],
  ["ritorno", "Ritorno"],
  ["entrambi", "Entrambi"],
];
const EMPTY = { origin: "", zone: "", radius_km: 50, direction: "entrambi", notify: true, active: true };

export default function CarrierRoutesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => apiFetch("/carrier/routes").then(setItems).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setError(""); setShowForm(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ origin: r.origin || "", zone: r.zone || "", radius_km: r.radius_km ?? 50, direction: r.direction || "entrambi", notify: r.notify !== false, active: r.active !== false });
    setError("");
    setShowForm(true);
  };

  const save = async () => {
    if (!form.origin.trim() || !form.zone.trim()) { setError("Indica dove parti di solito e la zona che ti interessa."); return; }
    setError(""); setSaving(true);
    try {
      const body = JSON.stringify(form);
      if (editingId) {
        const updated = await apiFetch(`/carrier/routes/${editingId}`, { method: "PUT", body });
        setItems((old) => old.map((it) => (it.id === editingId ? updated : it)));
      } else {
        const created = await apiFetch("/carrier/routes", { method: "POST", body });
        setItems((old) => [created, ...old]);
      }
      setShowForm(false); setForm(EMPTY); setEditingId(null);
    } catch (e) {
      setError(e.message || "Impossibile salvare. Riprova.");
    } finally { setSaving(false); }
  };

  const toggle = async (r) => {
    const res = await apiFetch(`/carrier/routes/${r.id}/toggle`, { method: "PATCH" }).catch(() => null);
    if (res) setItems((old) => old.map((it) => (it.id === r.id ? { ...it, active: res.active } : it)));
  };
  const remove = async (r) => {
    await apiFetch(`/carrier/routes/${r.id}`, { method: "DELETE" }).catch(() => {});
    setItems((old) => old.filter((it) => it.id !== r.id));
  };

  const inputCls = "min-h-[46px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-primary";
  const label = "mt-2 block text-xs font-bold text-muted-foreground";

  return (
    <div className="space-y-4">
      <BackHeader />
      <div className="flex items-start justify-between">
        <div>
          <Eyebrow>VETTORE</Eyebrow>
          <PageTitle>Dove cerco carichi</PageTitle>
          <PageSubtitle>Imposta le zone in cui viaggi abitualmente e ricevi segnalazioni sui viaggi compatibili.</PageSubtitle>
        </div>
        <button data-testid="route-add-button" onClick={() => (showForm ? setShowForm(false) : openCreate())} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          {showForm ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </button>
      </div>

      {showForm ? (
        <Card className="space-y-1">
          <p className="mb-1 text-lg font-extrabold text-foreground">{editingId ? "Modifica rotta" : "Aggiungi rotta"}</p>
          <label className={label}>Parto spesso da</label>
          <input data-testid="route-origin-input" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} placeholder="Es. Caivano" className={inputCls} />
          <label className={label}>Zona che mi interessa</label>
          <input data-testid="route-zone-input" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Località, provincia o regione (es. Latina, Lazio)" className={inputCls} />
          <label className={label}>Raggio di interesse</label>
          <div className="flex flex-wrap gap-2">
            {RADII.map((r) => (
              <button key={r} type="button" data-testid={`route-radius-${r}`} onClick={() => setForm({ ...form, radius_km: r })} className={cn("rounded-full border px-3 py-2 text-xs font-bold", form.radius_km === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-card-foreground")}>{r} km</button>
            ))}
          </div>
          <label className={label}>Cerco carichi</label>
          <div className="flex gap-2">
            {DIRECTIONS.map(([val, lbl]) => (
              <button key={val} type="button" data-testid={`route-direction-${val}`} onClick={() => setForm({ ...form, direction: val })} className={cn("flex-1 rounded-xl border px-2 py-2 text-xs font-bold", form.direction === val ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-card-foreground")}>{lbl}</button>
            ))}
          </div>
          <button type="button" data-testid="route-notify-toggle" onClick={() => setForm({ ...form, notify: !form.notify })} className="mt-3 flex w-full items-center justify-between rounded-xl border border-border px-3 py-2.5">
            <span className="text-sm font-bold text-card-foreground">Avvisami quando trovo un viaggio compatibile</span>
            <span className={cn("flex h-6 w-11 items-center rounded-full px-0.5 transition-colors", form.notify ? "bg-primary" : "bg-muted")}>
              <span className={cn("h-5 w-5 rounded-full bg-white transition-transform", form.notify ? "translate-x-5" : "translate-x-0")} />
            </span>
          </button>
          {error ? <p data-testid="route-form-error" className="text-sm font-bold text-destructive">{error}</p> : null}
          <button data-testid="route-save-button" onClick={save} disabled={saving} className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {saving ? "Salvataggio..." : editingId ? "SALVA MODIFICHE" : "SALVA ROTTA"}
          </button>
        </Card>
      ) : null}

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <EmptyState icon={Route} title="Nessuna rotta impostata" text="Aggiungi le tue zone abituali per ricevere segnalazioni sui viaggi utili." />
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} data-testid={`route-card-${r.id}`} className={cn("space-y-2", r.active === false ? "opacity-60" : "")}>
              <div className="flex items-center gap-2 font-extrabold text-foreground">
                <span className="truncate">{r.origin}</span>
                {r.direction === "entrambi" ? <ArrowLeftRight className="h-4 w-4 shrink-0 text-primary" /> : <ArrowRight className="h-4 w-4 shrink-0 text-primary" />}
                <span className="truncate">{r.zone}</span>
              </div>
              <p className="text-[13px] text-muted-foreground">
                Raggio: {r.radius_km} km · Direzione: {r.direction === "andata" ? "Andata" : r.direction === "ritorno" ? "Ritorno" : "Entrambi"} · Notifiche: {r.notify !== false ? "Attive" : "Off"}
                {r.active === false ? " · (disattivata)" : ""}
              </p>
              <div className="flex gap-2 pt-1">
                <button data-testid={`route-edit-${r.id}`} onClick={() => openEdit(r)} className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-extrabold text-primary"><Pencil className="h-4 w-4" /> Modifica</button>
                <button data-testid={`route-toggle-${r.id}`} onClick={() => toggle(r)} className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-extrabold text-card-foreground"><Power className="h-4 w-4" /> {r.active === false ? "Riattiva" : "Disattiva"}</button>
                <button data-testid={`route-delete-${r.id}`} onClick={() => remove(r)} className="flex items-center gap-1.5 rounded-lg bg-destructive/10 px-3 py-2 text-xs font-extrabold text-destructive"><Trash2 className="h-4 w-4" /> Elimina</button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
